import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { IconTrashcan } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { useModalContext } from 'context/modalContext'
import CreateMessage from 'features/Messaging/CreateMessage/CreateMessage'
import InboxContainer from 'features/Messaging/InboxContainer/InboxContainer'
import Message from 'features/Messaging/Message/Message'
import {
    deleteWelcomeMessage,
    getWelcomeMessage,
    setWelcomeMessage,
    updateWelcomeMessage
} from 'services/endpoints/settings/general/chat'
import { useUserProfile } from 'store'
import { type IProgressInfo } from 'types/interfaces/ITypes'

export const WelcomeMessage = () => {
    const queryClient = useQueryClient()
    const userData = useUserProfile()
    const modalData = useModalContext()
    const { t } = useTranslation()

    const [isEditing, setIsEditing] = useState(false)

    useEffect(() => {
        const viewport = document.querySelector<HTMLMetaElement>(
            'meta[name="viewport"]'
        )

        if (!viewport) return

        const previousContent = viewport.getAttribute('content') ?? ''

        viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, interactive-widget=resizes-content'
        )

        return () => {
            viewport.setAttribute('content', previousContent)
        }
    }, [])

    const [selectedFiles, setSelectedFiles] = useState<any[]>([])
    const [progressInfos, setProgressInfos] = useState<{
        val: IProgressInfo[]
    }>({
        val: []
    })

    const {
        data: welcomeMessageResponse,
        isLoading: isWelcomeMessageLoading,
        isError: isWelcomeMessageError
    } = useQuery(['welcome-message'], getWelcomeMessage, {
        staleTime: 60_000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: false
    })

    const welcomeMessage =
        welcomeMessageResponse?.data ?? welcomeMessageResponse ?? null

    const createWelcomeMessageMutation = useMutation(
        (params: any) => setWelcomeMessage(params),
        {
            onSuccess: async () => {
                setSelectedFiles([])
                setProgressInfos({
                    val: []
                })

                await queryClient.invalidateQueries(['welcome-message'])

                setIsEditing(false)
            },
            onError: () => {
                addToast(
                    'error',
                    'Something went wrong, please try again.'
                )
            }
        }
    )

    const updateWelcomeMessageMutation = useMutation(
        (params: any) => updateWelcomeMessage(params),
        {
            onSuccess: async () => {
                setSelectedFiles([])
                setProgressInfos({
                    val: []
                })

                await queryClient.invalidateQueries(['welcome-message'])

                setIsEditing(false)
            },
            onError: () => {
                addToast(
                    'error',
                    'Something went wrong, please try again.'
                )
            }
        }
    )

    const deleteWelcomeMessageMutation = useMutation(
        () => deleteWelcomeMessage(),
        {
            onSuccess: async () => {
                modalData.clearModal()

                queryClient.setQueryData(['welcome-message'], null)

                await queryClient.invalidateQueries(['welcome-message'])
            },
            onError: () => {
                addToast(
                    'error',
                    'Something went wrong, please try again.'
                )
            }
        }
    )
// HIDE HEADER WHEN KEYBOARD OPEN
    useEffect(() => {
        const header = document.querySelector<HTMLElement>('.whs__header')
        const viewport = window.visualViewport

        if (!header || !viewport) return

        const originalDisplay = header.style.display
        const initialHeight = window.innerHeight

        const updateKeyboard = () => {
            const keyboardOpen =
                window.innerWidth < 768 &&
                initialHeight - viewport.height > 150

            header.style.display = keyboardOpen ? 'none' : originalDisplay
        }

        updateKeyboard()

        viewport.addEventListener('resize', updateKeyboard)
        window.addEventListener('resize', updateKeyboard)

        return () => {
            viewport.removeEventListener('resize', updateKeyboard)
            window.removeEventListener('resize', updateKeyboard)

            header.style.display = originalDisplay
        }
    }, [])

    function openEdit() {
        setIsEditing(true)
    }

    const hasWelcomeMessage =
        Boolean(welcomeMessage?.message) ||
        Boolean(welcomeMessage?.body) ||
        Boolean(welcomeMessage?.photos?.length) ||
        Boolean(welcomeMessage?.videos?.length) ||
        Boolean(welcomeMessage?.sounds?.length)

    if (isWelcomeMessageLoading) {
        return <Container />
    }

    if (isWelcomeMessageError) {
        return (
            <Container>
                <div className='welcome-message-error'>
                    Unable to load welcome message.
                </div>
            </Container>
        )
    }

    const showComposer = isEditing || !hasWelcomeMessage

    return (
        <Container>
            {showComposer ? (
                <CreateMessage
                    progressInfos={progressInfos}
                    setProgressInfos={setProgressInfos}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    sendMessage={(value: any) => {
                        if (isEditing) {
                            updateWelcomeMessageMutation.mutate(value)
                            return
                        }

                        createWelcomeMessageMutation.mutate(value)
                    }}
                    writeMessageCustomClass='welcome-message-create-message'
                    hasGif={false}
                    welcomeMessage={true}
                />
            ) : (
                <>
                    <InboxContainer customClass='welcome-message-inbox'>
                        <Message
                            message={{
                                body:
                                    welcomeMessage?.message ??
                                    welcomeMessage?.body ??
                                    '',
                                photos: welcomeMessage?.photos ?? [],
                                videos: welcomeMessage?.videos ?? [],
                                sounds: welcomeMessage?.sounds ?? [],
                                recieved: false
                            }}
                            addReaction={() => null}
                            deleteMessage={() => null}
                            setReply={() => null}
                            recieved={false}
                            sender={userData}
                            reactions={[]}
                            reportMessage={() => null}
                            purchaseMessage={() => null}
                            welcomeMessage={true}
                        />
                    </InboxContainer>

                    <div className='welcome-message-buttons-container'>
                        <div
                            className='welcome-message-delete'
                            onClick={() => {
                                modalData.addModal(
                                    t('deleteMessage'),
                                    <ConfirmModal
                                        body={t('onceYouDeleteAMessage')}
                                        confirmFn={() => {
                                            deleteWelcomeMessageMutation.mutate()
                                        }}
                                    />
                                )
                            }}
                        >
                            <IconTrashcan width='20' height='22' />
                        </div>

                        <Button
                            text='Edit'
                            color='black'
                            customClass='welcome-message-button'
                            disabled={false}
                            clickFn={openEdit}
                        />
                    </div>
                </>
            )}
        </Container>
    )
}

export const Container = styled.div`
    height: 100%;
    overflow: hidden;
    position: relative;
`